import { autoinject, transient } from "aurelia-framework";
import { validateTrigger, ValidationController, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../../client-validation-system/custom-validation-renderer";
import { EmailValidationRequest, SubmitGhgRatingChangeRequest } from "../../../../../models/ghgs/ghg.models";
import { LookupValue } from "../../../../../models/lookups/lookup.models";
import { NotificationClientService, NotificationType } from "../../../../../services/basics/notification-client.service";
import { ValidationsClientService } from "../../../../../services/validations/validations-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { GhgService } from "../../../../../services/ghg/ghg.service";
import { EmailValidator } from "../../../../../utilities/email-validator";

@autoinject()
@transient()
export class RequestGhgRatingChange {

    public firstName: string;
    public lastName: string;
    public companyName: string;
    public selectedCompanyType: string;
    public readonly companyTypes: { id: number, name: string }[] = [
        {
            id: 1,
            name: 'Charterer - Bulk'
        },
        {
            id: 2,
            name: 'Charterer - Tanker'
        },
        {
            id: 3,
            name: 'Class'
        },
        {
            id: 4,
            name: 'Financial'
        },
        {
            id: 5,
            name: 'Flag'
        },
        {
            id: 6,
            name: 'Insurance'
        },
        {
            id: 7,
            name: 'Other'
        },
        {
            id: 8,
            name: 'P&I Club'
        },
        {
            id: 9,
            name: 'Port'
        },
        {
            id: 10,
            name: 'Ship Manager'
        },
        {
            id: 11,
            name: 'Ship Owner'
        },
        {
            id: 12,
            name: 'Terminal'
        },
    ]
    public jobTitle: string;
    public emailCorrespondences: any;
    public terms: boolean;
    public marketing: boolean;
    public country: LookupValue;
    public countryName: string;

    constructor(public validationsClientService: ValidationsClientService, private notificationService: NotificationClientService,
        private validationController: ValidationController, private dialogService: DialogClientService,
        private ghgService: GhgService) {
        this.validationController.addRenderer(new CustomValidationRenderer());
        this.validationController.changeTrigger(validateTrigger.manual);
    }

    attached() {
        this.setValidations();
    }

    setValidations() {
        ValidationRules
            .ensure<RequestGhgRatingChange, any>(x => x.firstName)
            .required()
            .withMessage("First name is required")
            
            .ensure(x => x.lastName)
            .required()
            .withMessage("Last name is required")

            .ensure(x => x.companyName)
            .required()
            .withMessage("Company name is required")

            .ensure(x => x.selectedCompanyType)
            .required()
            .withMessage("Company type is required")

            .ensure(x => x.country)
            .required()
            .withMessage("Country is required")

            .ensure(x => x.jobTitle)
            .required()
            .withMessage("Job title is required")
            
            .ensure(x => x.emailCorrespondences)
            .required()
            .satisfies( x=> EmailValidator.isValidEmail(x))
            .withMessage("Your email address for correspondence is required")

            .ensure(x => x.terms)
            .satisfies(x => x == true)
            .withMessage("Please agree to terms and Conditions")
            
            .on(this);

        this.validationController.addObject(this);
    }

    openNewTab(url: string, $event) {
        $event.preventDefault();
        $event.stopPropagation();
        const win = window.open(url, '_blank');
        win.focus();
    }

    public selectCountryChanged(model: any) {
        this.countryName = model.name;
    }

    async next() {
        try {
            const onValidationError = (validationResult: any) => {
                this.validationsClientService.showValidationErrors(validationResult);
            }

            const validationResult = await this.validationController.validate();
            if (validationResult.valid) {
                const model = {
                    firstName: this.firstName,
                    lastName: this.lastName,
                    jobTitle: this.jobTitle,
                    companyName: this.companyName,
                    companyType: this.selectedCompanyType,
                    country: this.countryName,                    
                    contactEmail: this.emailCorrespondences,
                    agreeToTermsAndConditions: this.terms,
                    agreeToReceiveMarketingCommunications: this.marketing
                } as SubmitGhgRatingChangeRequest;

                const result = await this.ghgService.validateEmailDomain({contactEmail: model.contactEmail} as EmailValidationRequest);

                if (result && result.hasError) {
                    onValidationError(result);
                } else {
                this.dialogService.show(RightShipDialogServiceConstants.URLs.DisclaimerConfirmationDialog,
                    { model: model, onValidationError: onValidationError }, true);
                }
            }
        }
        catch { 
            this.notificationService.show("Error when submitting form", "", NotificationType.Error, 5000);
        }
    }
}